import { REGEX } from '@repo/lib/constants';
import { assert } from './assert.js';

export const DEFAULT_REDACTION = '********';

type Redaction = {
  name: string;
  pattern: RegExp;
  replacement: string;
};

/**
 * Redacts PII or sensitive information in a string based on specified patterns
 * @param str The string to process
 * @param patterns Optional additional redaction patterns
 * @returns String with sensitive information redacted
 */
export const redactSensitiveInfo = (
  str: string,
  patterns: Redaction[] = [],
): string => {
  // Default pattern for email addresses
  const defaultPatterns: Redaction[] = [
    {
      name: 'email',
      pattern: REGEX.EMAIL_GLOBAL,
      replacement: DEFAULT_REDACTION,
    },
    {
      name: 'token',
      pattern: REGEX.JWT_GLOBAL,
      replacement: DEFAULT_REDACTION,
    },
    {
      name: 'zipCode',
      pattern: REGEX.ZIP_CODE_GLOBAL,
      replacement: DEFAULT_REDACTION,
    },
  ];

  // Combine custom and default patterns
  // We want to apply custom patterns first to ensure they take precedence
  const allPatterns = [...patterns, ...defaultPatterns];

  // Apply each pattern
  let result = str;
  allPatterns.forEach(({ pattern, replacement }) => {
    result = result.replace(pattern, replacement);
  });

  return result;
};

const graphemeSegmenters = new Map<string, Intl.Segmenter>();

/** Cached `Intl.Segmenter` for grapheme clusters; pass a BCP 47 locale tag. */
const getGraphemeSegmenter = (locale: string) => {
  let segmenter = graphemeSegmenters.get(locale);
  if (!segmenter) {
    segmenter = new Intl.Segmenter(locale, { granularity: 'grapheme' });
    graphemeSegmenters.set(locale, segmenter);
  }
  return segmenter;
};

/**
 * The first `count` grapheme clusters of `text`.
 *
 * `String.prototype.slice` counts UTF-16 code units, so cutting a string at a
 * fixed length can land inside a surrogate pair and leave a lone half that
 * renders as a replacement glyph — an emoji or any non-BMP character at the
 * boundary. Anything truncating user-supplied or tenant-supplied text for
 * display wants this instead.
 *
 * Counts what a reader sees rather than what the encoding stores, so a combining
 * accent or a ZWJ sequence also survives as one unit.
 */
export const sliceGraphemes = (
  text: string,
  count: number,
  locale = 'en',
): string => {
  if (count <= 0) return '';
  let result = '';
  let taken = 0;
  for (const { segment } of getGraphemeSegmenter(locale).segment(text)) {
    if (taken >= count) break;
    result += segment;
    taken += 1;
  }
  return result;
};

/**
 * Derives initials from a name string.
 * - "John Doe" → "JD"
 * - "John" → "J"
 * - "John Michael Doe" → "JD" (first + last)
 *
 * Unicode-safe: handles emoji and non-BMP characters correctly.
 */
export const getInitials = (name: string, locale = 'en'): string => {
  const names = name.split(/\s+/).filter(Boolean);

  const firstName = names[0];
  const lastName = names.length > 1 ? names[names.length - 1] : '';

  const segmenter = getGraphemeSegmenter(locale);
  const initials = [firstName, lastName].map((name) => {
    if (!name) return '';
    const segments = segmenter.segment(name);
    for (const seg of segments) {
      return seg.segment;
    }
    return '';
  });

  return initials.join('').toLocaleUpperCase(locale);
};

export function colorizeYaml(
  yaml: string,
  colorize: {
    key: (text: string) => string;
    value: (text: string) => string;
    multilineContent: (text: string) => string;
    multilineIndicator: (text: string) => string;
    comment: (text: string) => string;
    listItem: (text: string) => string;
  },
): string {
  const lines = yaml.split('\n');
  const result: string[] = [];
  let inMultilineString = false;
  let multilineIndent = 0;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    assert(line !== undefined);

    const currentIndent = (() => {
      const match = line.match(/^(\s*)/);
      assert(match !== null);
      const leadingSpaces = match[1];
      assert(leadingSpaces !== undefined);
      return leadingSpaces.length;
    })();

    // Check if we're continuing a multiline string
    if (inMultilineString) {
      // Multiline content continues if indented more than the key
      if (currentIndent > multilineIndent && line.trim() !== '') {
        result.push(colorize.multilineContent(line));
        continue;
      } else {
        // Multiline ended
        inMultilineString = false;
      }
    }

    // Match lines like "  key: value" or "  key: |-"
    const match = line.match(/^(\s*)([^:]+):\s*(.*)$/);
    if (match) {
      const [, indent = '', key = '', value = ''] = match;

      // Check if this starts a multiline string (|-, |, >, >-)
      if (value.match(/^[|>][-+]?$/)) {
        inMultilineString = true;
        multilineIndent = currentIndent;
        result.push(
          indent +
            colorize.key(key) +
            ': ' +
            colorize.multilineIndicator(value),
        );
        continue;
      }

      result.push(indent + colorize.key(key) + ': ' + colorize.value(value));
      continue;
    }

    // Comments or empty lines
    if (line.trim().startsWith('#') || line.trim() === '') {
      result.push(colorize.comment(line));
      continue;
    }

    // List items or other YAML constructs
    if (line.trim().startsWith('-')) {
      result.push(colorize.listItem(line));
      continue;
    }

    // Default: return unmodified
    result.push(line);
  }

  return result.join('\n');
}

/**
 * Converts a string to title case
 * @param value The string to convert
 * @param locale BCP 47 language tag for grapheme segmentation and word casing; defaults to English.
 * @returns The string in title case
 * @example
 * toTitleCase('Late delivery')
 * // => 'Late Delivery'
 */
export const toTitleCase = (value: string, locale = 'en'): string =>
  value
    .split(' ')
    .map((word) => toSentenceCase(word, locale))
    .join(' ');

/**
 * Converts a string to sentence case (first character uppercased).
 * Unicode-safe: handles emoji and non-BMP characters correctly.
 * @param value The string to convert
 * @param locale BCP 47 language tag for grapheme segmentation and uppercasing the first letter; defaults to English.
 * @returns The string in sentence case
 * @example
 * toSentenceCase('email contains @example.com')
 * // => 'Email contains @example.com'
 */
export const toSentenceCase = (value: string, locale = 'en'): string => {
  if (!value) return value;
  const segmenter = getGraphemeSegmenter(locale);
  const chars = Array.from(segmenter.segment(value)).map((seg) => seg.segment);
  if (chars[0]) {
    chars[0] = chars[0].toLocaleUpperCase(locale);
  }
  return chars.join('');
};

/**
 * Converts a snake_case field name to a human-readable sentence-case label.
 * Replaces underscores with spaces and capitalizes the first character.
 * @param name The snake_case field name
 * @returns Human-readable label
 * @example
 * humanizeFieldName('order_items')
 * // => 'Order items'
 * humanizeFieldName('product_sku')
 * // => 'Product sku'
 */
export const humanizeFieldName = (name: string): string =>
  toSentenceCase(name.replace(/_/g, ' '));

/**
 * Converts a string to a URL-friendly slug.
 * Lowercases, strips non-word characters (except spaces and hyphens),
 * replaces whitespace with hyphens, and collapses consecutive hyphens.
 * @param text The string to slugify
 * @returns URL-friendly slug
 * @example
 * slugify('Getting Started with APIs')
 * // => 'getting-started-with-apis'
 * slugify('What is @repo/ui?')
 * // => 'what-is-repoui'
 */
/**
 * Converts a kebab-case string to Title Case.
 * Splits on hyphens, capitalizes the first letter of each word, and joins with spaces.
 * @param value The kebab-case string to convert
 * @returns The string in Title Case
 * @example
 * kebabToTitleCase('acme-corp')
 * // => 'Acme Corp'
 * kebabToTitleCase('my-test-tenant')
 * // => 'My Test Tenant'
 */
export const kebabToTitleCase = (value: string): string =>
  value
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

export const slugify = (text: string): string =>
  text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
